import { api } from "./api";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PlanSummary {
  id: number;
  name: string;
  description: string | null;
  statusId: number | null;
  trialDays: number;
  isDefault: boolean;
}

export interface PlanCreateRequest {
  name: string;
  description?: string | null;
  trialDays?: number;
}

export interface PlanUpdateRequest {
  name?: string | null;
  description?: string | null;
  trialDays?: number | null;
  statusId?: number | null;
}

export interface PlanFeatureItem {
  featureId: number;
  featureKey: string | null;
  featureDesc: string | null;
  isResourceCap: boolean;
  monthlyLimit: number | null;
  trialMonthlyLimit: number | null;
}

export interface FeatureAssignItem {
  featureId: number;
  monthlyLimit?: number | null;
  trialMonthlyLimit?: number | null;
}

export interface FeatureLimitUpdate {
  monthlyLimit?: number | null;
  trialMonthlyLimit?: number | null;
}

export interface FeatureLimitResponse {
  featureId: number;
  monthlyLimit: number | null;
  trialMonthlyLimit: number | null;
}

// AI credit allowances for a plan. null = unlimited (no cap at all), which is also the default —
// a plan with no allowance serves AI without blocking. Credits are decimal(18,4) server-side.
export interface AiTierResponse {
  monthlyCredits: number | null;
  monthlyCreditsPerUser: number | null;
  trialMonthlyCredits: number | null;
}

// Partial update: an omitted/null field leaves that allowance unchanged. Going back to unlimited is
// an explicit action — the matching clear* flag, never a null.
export interface AiTierUpdateRequest {
  monthlyCredits?: number | null;
  monthlyCreditsPerUser?: number | null;
  trialMonthlyCredits?: number | null;
  clearMonthlyCredits?: boolean;
  clearMonthlyCreditsPerUser?: boolean;
  clearTrialMonthlyCredits?: boolean;
}

export interface SetDefaultPlanResponse {
  planId: number;
  isDefault: true;
}
// creditsUsed is confirmed spend (AiTokenLedger.CreditsCharged) plus a live conversion of
// whatever is still reserved/in-flight — never a live conversion of the confirmed figure, so a
// later change to conversion settings can't move a period's already-confirmed number.
export interface AiCreditStatusDto {
  monthlyCredits: number | null;
  creditsUsed: number;
  periodStart: string; // ISO 8601
  resetsAt: string; // ISO 8601
}

export interface AiCreditFeatureUsageDto {
  featureKey: string;
  featureName: string | null;
  featureNameEn: string | null;
  credits: number;
  callCount: number;
}

export interface AiCreditUserUsageDetailDto {
  userGuid: string;
  userName: string | null;
  monthlyCredits: number | null;
  creditsUsed: number;
  periodStart: string; // ISO 8601
  // This seat's own credit pack. null = no personal pack ever bought; 0 = bought and spent.
  purchasedBalance: number | null;
}

// What GET /FeatureCredits/Balance returns for the company, mirrored onto this response.
// company.creditsUsed includes purchased-pack spend and in-flight reservations; allowanceRemaining
// counts only what the monthly allowance funded. The two will not reconcile — use this for the meter.
export interface AiCreditBalanceDto {
  monthlyQuota: number | null;
  allowanceRemaining: number;
  usedPercent: number;
  purchasedBalance: number;
  purchasedCompanyBalance: number;
  purchasedSeatBalance: number;
  resetsAt: string; // ISO 8601
  // The caller's own seat. Null when the company being viewed isn't the caller's own.
  user: { usedPercent: number; exhausted: boolean } | null;
}

export interface AiCreditCompanyDetailResponseDto {
  companyGuid: string;
  companyName: string | null;
  company: AiCreditStatusDto;
  balance: AiCreditBalanceDto;
  features: AiCreditFeatureUsageDto[];
  users: AiCreditUserUsageDetailDto[];
}

// ─── Endpoints ───────────────────────────────────────────────────────────────

export const plansAdminApi = api.injectEndpoints({
  endpoints: (build) => ({
    listPlans: build.query<PlanSummary[], { active?: boolean } | void>({
      query: (arg) => ({
        url: "admin/plans",
        params: arg?.active === undefined ? undefined : { active: arg.active },
      }),
    }),

    getPlan: build.query<PlanSummary, number>({
      query: (id) => ({ url: `admin/plans/${id}` }),
    }),

    createPlan: build.mutation<PlanSummary, PlanCreateRequest>({
      query: (body) => ({ url: "admin/plans", method: "POST", body }),
    }),

    updatePlan: build.mutation<PlanSummary, { id: number; body: PlanUpdateRequest }>({
      query: ({ id, body }) => ({ url: `admin/plans/${id}`, method: "PUT", body }),
    }),

    deletePlanById: build.mutation<void, number>({
      query: (id) => ({ url: `admin/plans/${id}`, method: "DELETE" }),
    }),

    getPlanFeatures: build.query<PlanFeatureItem[], number>({
      query: (id) => ({ url: `admin/plans/${id}/features` }),
    }),

    setPlanFeatures: build.mutation<void, { id: number; items: FeatureAssignItem[] }>({
      query: ({ id, items }) => ({ url: `admin/plans/${id}/features`, method: "PUT", body: items }),
    }),

    addPlanFeature: build.mutation<void, { id: number; featureId: number; body?: FeatureLimitUpdate }>({
      query: ({ id, featureId, body }) => ({ url: `admin/plans/${id}/features/${featureId}`, method: "POST", body }),
    }),

    removePlanFeature: build.mutation<void, { id: number; featureId: number }>({
      query: ({ id, featureId }) => ({ url: `admin/plans/${id}/features/${featureId}`, method: "DELETE" }),
    }),

    setFeatureLimit: build.mutation<FeatureLimitResponse, { id: number; featureId: number; body: FeatureLimitUpdate }>({
      query: ({ id, featureId, body }) => ({ url: `admin/plans/${id}/features/${featureId}/limit`, method: "PUT", body }),
    }),

    getAiTier: build.query<AiTierResponse, number>({
      query: (id) => ({ url: `admin/plans/${id}/ai-tier` }),
      providesTags: (_result, _error, id) => [{ type: "AiTier", id }],
    }),

    upsertAiTier: build.mutation<AiTierResponse, { id: number; body: AiTierUpdateRequest }>({
      query: ({ id, body }) => ({ url: `admin/plans/${id}/ai-tier`, method: "PUT", body }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "AiTier", id }],
    }),

    setDefaultPlan: build.mutation<SetDefaultPlanResponse, number>({
      query: (id) => ({ url: `admin/plans/${id}/default`, method: "PUT" }),
    }),
    getCompanyUsageDetail: build.query<AiCreditCompanyDetailResponseDto, string>({
      query: (companyGuid) => `AiQuotasAdmin/usage/${companyGuid}`,
    }),
  }),
});

export const {
  useListPlansQuery,
  useGetPlanQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useDeletePlanByIdMutation,
  useGetPlanFeaturesQuery,
  useSetPlanFeaturesMutation,
  useAddPlanFeatureMutation,
  useRemovePlanFeatureMutation,
  useSetFeatureLimitMutation,
  useGetAiTierQuery,
  useUpsertAiTierMutation,
  useSetDefaultPlanMutation,
  useGetCompanyUsageDetailQuery,
} = plansAdminApi;
